// SelectDeviceModal.jsx — "Select Device" popup
//
// Flow:
//   1. On open: fetch the customer tree, flatten it, auto-select the
//      first customer in the list.
//   2. Whenever the selected customer changes: fetch that customer's
//      devices and show them in the right-hand table with checkboxes.
//   3. User ticks devices, clicks "Select Device" -> onConfirm({ deviceNames,
//      deviceIds, devices }) is called, then the modal closes.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getCustomerTree, getDevicesByCustomerId } from "../utility";


function flattenCustomerTree(nodes = []) {
  const out = [];
  const walk = (list) => {
    for (const node of list) {
      out.push({
        customerId: node.customerId ?? node.id,
        customerName: node.customerName ?? node.name,
      });
      if (Array.isArray(node.children) && node.children.length) {
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return out;
}

function getDeviceId(device) {
  const id = device.deviceId ?? device.imei ?? device.id ?? device.deviceIdOrName;
  if (id === undefined || id === null || id === "") {
    // eslint-disable-next-line no-console
    console.warn(
      "[SelectDeviceModal] Could not find a device identifier on this record — " +
        "checked deviceId, imei, id, deviceIdOrName. Update getDeviceId() with the " +
        "correct field name. Record was:",
      device,
    );
  }
  return id;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SelectDeviceModal = ({ open, onClose, onConfirm, initialSelectedDevices = [] }) => {
  // Left panel: customer tree
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState(null);

  // Right panel: devices for the selected customer
  const [devices, setDevices] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState(null);

  // Selection: array of { label, value } — value is the resolved device id
  const [selectedDevices, setSelectedDevices] = useState([]);

  // ── Load customer tree on open ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadTree = async () => {
      setTreeLoading(true);
      setTreeError(null);
      try {
        const response = await getCustomerTree();
        if (response.code !== 0) throw new Error(response.msg || "Failed to load customers");

        const flat = flattenCustomerTree(response.data || []);
        if (cancelled) return;

        setCustomers(flat);
        // Auto-select the first customer in the tree
        if (flat.length > 0) setSelectedCustomerId(flat[0].customerId);
      } catch (err) {
        if (!cancelled) setTreeError(err.message ?? "Failed to load customers");
      } finally {
        if (!cancelled) setTreeLoading(false);
      }
    };

    loadTree();
    return () => { cancelled = true; };
  }, [open]);

 
  useEffect(() => {
    if (open) setSelectedDevices(initialSelectedDevices);
  }, [open, initialSelectedDevices]);

  // ── Load devices whenever the selected customer changes ────────────────────
  const loadDevices = useCallback(async (customerId, search = "") => {
    if (!customerId) return;
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const response = await getDevicesByCustomerId({
        customerId,
        deviceIdOrName: search,
      });
      if (response.code !== 0) throw new Error(response.msg || "Failed to load devices");
      setDevices(response.data?.records ?? response.data ?? []);
    } catch (err) {
      setDevicesError(err.message ?? "Failed to load devices");
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCustomerId) loadDevices(selectedCustomerId, deviceSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId]);

  // ── Derived: customer list filtered by search box ──────────────────────────
  const visibleCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.trim().toLowerCase();
    return customers.filter((c) => c.customerName?.toLowerCase().includes(q));
  }, [customers, customerSearch]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleCustomerClick = (customerId) => setSelectedCustomerId(customerId);

  const handleDeviceSearchSubmit = () => loadDevices(selectedCustomerId, deviceSearch);

  const isDeviceSelected = (device) =>
    selectedDevices.some((d) => d.value === getDeviceId(device));

  const toggleDevice = (device) => {
    const id = getDeviceId(device);
    setSelectedDevices((prev) =>
      isDeviceSelected(device)
        ? prev.filter((d) => d.value !== id)
        : [...prev, { label: device.deviceName, value: id }],
    );
  };

  const allOnPageSelected = devices.length > 0 && devices.every(isDeviceSelected);

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      const pageIds = new Set(devices.map(getDeviceId));
      setSelectedDevices((prev) => prev.filter((d) => !pageIds.has(d.value)));
    } else {
      setSelectedDevices((prev) => {
        const existing = new Set(prev.map((d) => d.value));
        const additions = devices
          .filter((d) => !existing.has(getDeviceId(d)))
          .map((d) => ({ label: d.deviceName, value: getDeviceId(d) }));
        return [...prev, ...additions];
      });
    }
  };

  const clearAllSelected = () => setSelectedDevices([]);

  // ── Save: build the two comma-joined strings the parent page sends to the API ──
  const handleSave = () => {
    const deviceNames = selectedDevices.map((d) => d.label).join(",");
    const deviceIds = selectedDevices.map((d) => d.value).join(",");
    onConfirm({ deviceNames, deviceIds, devices: selectedDevices });
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "16px", fontWeight: 600 }}>
        Select Device
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Alert severity="warning" sx={{ borderRadius: 0, fontSize: "13px" }}>
          If you notice that some devices cannot be selected, it may be that the device is
          inactive, or the subordinate customer has turned off the &quot;Allow supervisor to
          view video&quot; switch.
        </Alert>

        <Box sx={{ display: "flex", height: 380 }}>
          {/* ── Left: customer tree ──────────────────────────────────────── */}
          <Box sx={{ width: 220, borderRight: "1px solid #e4e7ef", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Please enter customer name"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { fontSize: "13px" } }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {treeLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {treeError && (
                <Typography sx={{ fontSize: "12px", color: "#b91c1c", p: 1 }}>{treeError}</Typography>
              )}
              {!treeLoading && !treeError && visibleCustomers.map((c) => (
                <Box
                  key={c.customerId}
                  onClick={() => handleCustomerClick(c.customerId)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    fontSize: "13px",
                    cursor: "pointer",
                    color: c.customerId === selectedCustomerId ? "#fff" : "#d97706",
                    background: c.customerId === selectedCustomerId ? "#082D6B" : "transparent",
                    "&:hover": {
                      background: c.customerId === selectedCustomerId ? "#082D6B" : "#f0f2f7",
                    },
                  }}
                >
                  {c.customerName}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Right: device table ──────────────────────────────────────── */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 1, display: "flex", gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Please enter the IMEI or Device ID"
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleDeviceSearchSubmit()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleDeviceSearchSubmit}>
                        <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { fontSize: "13px" } }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {devicesLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {devicesError && (
                <Typography sx={{ fontSize: "12px", color: "#b91c1c", p: 1 }}>{devicesError}</Typography>
              )}

              {!devicesLoading && !devicesError && (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allOnPageSelected}
                          indeterminate={!allOnPageSelected && devices.some(isDeviceSelected)}
                          onChange={toggleSelectAllOnPage}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>Device Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>IMEI</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>Label</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>Model</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((d) => (
                      <TableRow key={getDeviceId(d) ?? d.deviceName} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isDeviceSelected(d)}
                            onChange={() => toggleDevice(d)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{d.deviceName}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{d.id}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{d.label ?? ""}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{d.deviceType}</TableCell>
                      </TableRow>
                    ))}
                    {devices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: "center", color: "#94a3b8", fontSize: "13px", py: 4 }}>
                          No devices found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 2, py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
            Total: {selectedDevices.length}
          </Typography>
          <IconButton size="small" onClick={clearAllSelected} disabled={selectedDevices.length === 0}>
            <DeleteOutlineIcon sx={{ fontSize: 18, color: selectedDevices.length ? "#64748b" : "#cbd5e1" }} />
          </IconButton>
        </Box>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={selectedDevices.length === 0}
          sx={{ textTransform: "none", background: "#082D6B", "&:hover": { background: "#0a3580" } }}
        >
          Select Device
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectDeviceModal;